import { maxFramerate } from 'src/types/Match';
import { ControlledPromise } from 'src/utils/controlled-promise';
import { attachMediaElement } from 'src/utils/media/view';
import { measureAsync, recordVideoFrame, VideoFpsSource } from 'src/utils/perf';

import MediaStreamPipe, { PipeInputEl } from './base';

class MediaStreamPipeManager extends MediaStreamPipe {
  private timerId: ReturnType<typeof setInterval> | null = null;

  public firstFrameRenderedPromise = new ControlledPromise<void>();

  private markFirstFrameRenderedTimerId: ReturnType<typeof setTimeout> | null = null;

  private canvasAnimationId: number | null = null;

  private supportVideoFrameCallback = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

  private readonly boundHandleChangeVisibility = this.handleChangeVisibility.bind(this);

  private readonly boundHandleResizeSourceTrack = this.handleResizeSourceTrack.bind(this);

  public pipes: MediaStreamPipe[] = [];

  /** 설정하면 render()마다 해당 source로 videoFps를 기록한다. null이면 기록 안 함. */
  public videoFpsSource: VideoFpsSource | null = null;

  /** render()가 실제로 새 프레임을 그릴 때마다 증가한다. canvas를 입력으로 받는 MediaStreamPipeManager를
   * 체이닝할 때, 소스 매니저의 새 프레임 여부를 판단하는 데 쓴다.
   */
  public frameVersion = 0;

  /**
   * inputEl이 canvas일 때만 의미가 있다. canvas는 video의 requestVideoFrameCallback
   * 같은 "새 프레임 도착" 신호가 없어서, 매 rAF마다 무조건 다시 그리면 상류가 그대로인
   * 프레임까지 반복해서 그리게 된다 (WEB-5529 벤치마크로 확인된 회귀). 설정하면 render()가
   * 이 값이 실제로 바뀌었을 때만 그린다.
   */
  private getSourceFrameVersion: (() => number) | null = null;

  private lastRenderedSourceFrameVersion = -1;

  /**
   * 실제 출력 canvas 크기가 확정/변경됐을 때 알린다. start() 반환 시점에는 아직 첫 render 전이라
   * 더미 크기(기본 300×150)가 반환되는데, 이 값을 인코더(scaleResolutionDownBy)의 근거로
   * 쓰면 실제 회전 프레임이 스케일링 없이 송출된다 (WEB-5529). 크기가 정해진 뒤 이 리스너로
   * 실제 크기를 보고한다.
   */
  private lastReportedOutputWidth = 0;

  private lastReportedOutputHeight = 0;

  private onOutputSizeChange: ((width: number, height: number) => void) | null = null;

  public setOutputSizeListener(listener: ((width: number, height: number) => void) | null) {
    this.onOutputSizeChange = listener;
  }

  public setFrameVersionSource(getSourceFrameVersion: typeof this.getSourceFrameVersion) {
    this.getSourceFrameVersion = getSourceFrameVersion;
    this.lastRenderedSourceFrameVersion = -1;
  }

  public pipe(pipe: MediaStreamPipe) {
    this.pipes.push(pipe);
    return this;
  }

  /**
   * canvas를 입력으로 받는 매니저에서만 쓴다. 입력 canvas가 리마운트된 경우(예: camera on/off, 크리에이터 뷰모드 전환)
   * 입력 canvas를 새것으로 업데이트 해야한다.
   *
   * 렌더 루프가 진행 중이면 getter 결과로 inputEl과 마지막 파이프의 입력을 갱신하고,
   * 아직 start() 전이면 start() 시점에 첫 getter 결과를 적용한다.
   */
  private inputElGetter: (() => PipeInputEl | null) | null = null;

  public setInputElGetter(inputElGetter: (() => PipeInputEl | null) | null) {
    this.inputElGetter = inputElGetter;
  }

  /** getter를 사용해서 이 매니저(와 마지막 파이프)가 그릴 입력을 현재 값으로 맞춘다. */
  private applyInputElGetter() {
    if (!this.inputElGetter) {
      return;
    }
    const inputEl = this.inputElGetter();
    if (!inputEl || inputEl === this.inputEl) {
      return;
    }
    this.inputEl = inputEl;
    this.lastRenderedSourceFrameVersion = -1;
    const lastPipe = this.pipes[this.pipes.length - 1];
    if (lastPipe) {
      lastPipe.setEl(inputEl, this.outputCanvasEls);
    }
  }

  public unpipe(target: MediaStreamPipe) {
    this.stopRendering();
    this.pipes = this.pipes.filter((pipe) => pipe !== target);
    if (this.inputEl) {
      this.start();
    } else {
      this.dispose();
    }
  }

  /**
   * @param forceCleanup true일 경우 관련된 모든 파이프와 리소스를 정리하고, 기존의 렌더링 결과물을 비움 (default=false)
   */
  public dispose(forceCleanup = false) {
    if (this.mediaStream || this.inputEl) {
      this.stopRendering();
    }
    if (forceCleanup) {
      this.pipes.forEach((pipe) => {
        pipe.dispose();
      });
      this.outputCanvasEls.forEach((canvas) => {
        canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
      });
    }
    this.pipes = [];
    this.inputEl = null;
  }

  private renderCheckBackground() {
    if (document.hidden && !this.timerId) {
      this.timerId = setInterval(this.render.bind(this), 1000 / maxFramerate);
    }

    this.render();
  }

  private handleChangeVisibility() {
    if (!document?.hidden && this.timerId) {
      this.clearLoop();
      this.render();
      return;
    }

    this.renderCheckBackground();
  }

  public stopRendering() {
    this.clearLoop();
    window.removeEventListener('visibilitychange', this.boundHandleChangeVisibility);
    if (this.inputEl) {
      this.inputEl.removeEventListener('resize', this.boundHandleResizeSourceTrack);
    }
    this.pipes.forEach((pipe, i) => {
      pipe.stopRendering();
      if (i !== 0) {
        pipe.dispose();
      }
    });
  }

  protected clearLoop() {
    if (this.canvasAnimationId) {
      if (this.inputEl instanceof HTMLVideoElement && this.supportVideoFrameCallback) {
        this.inputEl.cancelVideoFrameCallback(this.canvasAnimationId);
      } else {
        cancelAnimationFrame(this.canvasAnimationId);
      }
      this.canvasAnimationId = null;
    }
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private handleResizeSourceTrack() {
    if (!(this.inputEl instanceof HTMLVideoElement) || !this.inputEl.srcObject) {
      return;
    }
    if (this.inputEl.srcObject instanceof MediaStream) {
      const videoTrack = this.inputEl.srcObject.getVideoTracks()?.[0];
      if (videoTrack) {
        const { width = 0, height = 0 } = videoTrack.getSettings();
        this.inputEl.width = width;
        this.inputEl.height = height;
        this.renderVideo();
      }
    }
  }

  public start() {
    // canvas 입력 매니저라면, start() 시점의 현재 입력을 먼저 확정한다 (setEl 없이 getter만 주고 start() 하는 경우).
    this.applyInputElGetter();

    if (!this.inputEl) {
      throw new Error('Cannot start MediaStreamPipe. inputEl not set');
    }
    window.addEventListener('visibilitychange', this.boundHandleChangeVisibility);

    if (this.outputCanvasEls.length === 0) {
      this.outputCanvasEls = [document?.createElement('canvas')];
    }

    const outputCanvasEls = this.outputCanvasEls;
    const firstCanvas = outputCanvasEls[0];

    this.updateMediaStream();
    if (this.inputEl instanceof HTMLVideoElement) {
      this.inputEl.addEventListener('resize', this.boundHandleResizeSourceTrack);
    }

    this.pipes.reduce((prevVideo, pipe, i) => {
      const isLast = i === this.pipes.length - 1;
      if (isLast) {
        pipe.setEl(prevVideo, outputCanvasEls);
        return prevVideo;
      } else {
        pipe.setEl(prevVideo);
        pipe.updateMediaStream();
        const pipeMediaStream = pipe.mediaStream;
        if (!pipeMediaStream) {
          return prevVideo;
        }
        const nextVideo = document?.createElement('video');
        nextVideo.addEventListener('resize', this.boundHandleResizeSourceTrack);
        attachMediaElement(nextVideo, pipeMediaStream);
        return nextVideo;
      }
    }, this.inputEl);
    this.renderVideo();
    return {
      mediaStream: this.mediaStream,
      width: firstCanvas.width,
      height: firstCanvas.height,
    };
  }

  public renderVideo() {
    this.clearLoop();
    this.pipes.forEach((pipe) => {
      pipe.renderVideo();
    });

    this.renderCheckBackground();
  }

  /** 첫 출력 canvas의 실제 크기가 확정/변경됐으면 리스너로 보고한다. */
  private reportOutputSize() {
    if (!this.onOutputSizeChange) {
      return;
    }
    const firstCanvas = this.outputCanvasEls[0];
    if (!firstCanvas || !firstCanvas.width || !firstCanvas.height) {
      return;
    }
    if (
      firstCanvas.width === this.lastReportedOutputWidth &&
      firstCanvas.height === this.lastReportedOutputHeight
    ) {
      return;
    }
    this.lastReportedOutputWidth = firstCanvas.width;
    this.lastReportedOutputHeight = firstCanvas.height;
    this.onOutputSizeChange(firstCanvas.width, firstCanvas.height);
  }

  public async render() {
    const outputCanvasEls = this.outputCanvasEls;
    if (outputCanvasEls.length === 0) {
      return;
    }

    // 캔버스 입력이 리마운트로 교체됐으면, 이번 프레임부터 새 입력을 그린다.
    this.applyInputElGetter();

    const inputEl = this.inputEl;
    if (!inputEl) {
      return;
    }

    if (this.getSourceFrameVersion) {
      const sourceFrameVersion = this.getSourceFrameVersion();
      if (sourceFrameVersion === this.lastRenderedSourceFrameVersion) {
        // 상류(canvas 입력)에 새 프레임이 없다 — video의 requestVideoFrameCallback과 달리
        // canvas는 "새 프레임 도착" 신호가 없어서 직접 dirty-check해야 한다 (WEB-5529).
        this.scheduleNextRender();
        return;
      }
      this.lastRenderedSourceFrameVersion = sourceFrameVersion;
    }

    const renderLabel = this.videoFpsSource
      ? (`pipe.render.${this.videoFpsSource}` as const)
      : ('pipe.render' as const);
    await measureAsync(renderLabel, async () => {
      for (const pipe of this.pipes) {
        await pipe.render.bind(pipe)();
      }
    });
    if (this.videoFpsSource) {
      recordVideoFrame(this.videoFpsSource);
    }
    this.reportOutputSize();
    this.frameVersion += 1;

    if (!this.firstFrameRenderedPromise.ended && this.markFirstFrameRenderedTimerId === null) {
      this.markFirstFrameRenderedTimerId = setTimeout(() => {
        /**
         * canvas draw 직후에는 캡쳐 등의 로직이 정상 동작하지 않을 수 있음
         * 약간의 딜레이 이후에 최초 프레임 렌더링되었다고 마킹
         */
        this.firstFrameRenderedPromise.resolve();
        this.markFirstFrameRenderedTimerId = null;
      }, 100);
    }

    this.scheduleNextRender();
  }

  private scheduleNextRender() {
    if (!this.timerId && this.inputEl) {
      const renderWithThisBind = this.render.bind(this);
      if (this.inputEl instanceof HTMLVideoElement && this.supportVideoFrameCallback) {
        this.canvasAnimationId = this.inputEl.requestVideoFrameCallback(renderWithThisBind);
      } else {
        this.canvasAnimationId = requestAnimationFrame(renderWithThisBind);
      }
    }
  }
}

export default MediaStreamPipeManager;
